// Module 19 (BL-380) G1 - Events board constants. The database stores neutral values; these are the words the screen shows.

export const EVENT_VIEW_MODES = ['Cards', 'List', 'Calendar'];
export const EVENT_VIEW_STORAGE_KEY = 'eventsBoardViewMode';

// engagements.status values an Events engagement can hold for now (the database pins it to these two).
// DC-1: the screen word for the stored status 'requested' is "In Planning"; the database is unchanged.
export const EVENT_STATUS_META = {
    requested: { label: 'In Planning' },
    canceled: { label: 'Canceled' },
};

// The status pills (each shows its count). 'planned' is the filter's own name for "not canceled"; the screen says In Planning.
export const EVENT_STATUS_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'planned', label: 'In Planning' },
    { value: 'canceled', label: 'Canceled' },
];

export const EVENT_WHEN_FILTERS = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past' },
    { value: 'all', label: 'Any date' },
];

// Shown until the organization's own starter kits load (and as a fallback if they cannot).
export const FALLBACK_EVENT_TYPES = [
    { key: 'wedding', label: 'Wedding', default_checklist: [] },
    { key: 'corporate', label: 'Corporate event', default_checklist: [] },
    { key: 'social', label: 'Social event', default_checklist: [] },
    { key: 'nonprofit_gala', label: 'Nonprofit gala', default_checklist: [] },
];

export const BLANK_EVENT_FORM = {
    event_name: '',
    event_date: '',
    end_date: '',
    event_type_other: '',
    start_time: '',
    end_time: '',
    venue_name: '',
    venue_address: '',
    event_type: '',
    expected_guest_count: '',
    notes: '',
    entity_id: '',
    plus_ones_enabled: false,
    plus_ones_max: 1,
    guest_transport_mode: 'client_arranged',
    transport_provider_name: '',
    coordinator_name: '',
    coordinator_phone: '',
    coordinator_email: '',
    guest_visibility: {},
    venue_vendor_id: null,
    provider_vendor_id: null,
    hotels: [],
};

// M19 T5: an "Other" event shows the name someone typed.
export function eventTypeName(kits, event) {
    return event?.event_type === 'other' && event?.event_type_other ? event.event_type_other : eventTypeLabel(kits, event?.event_type);
}

export function eventTypeLabel(kits, key) {
    if (!key) return 'Event';
    const kit = (kits || []).find((item) => item.key === key);
    if (kit?.label) return kit.label;
    return String(key).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function eventStatusLabel(status) {
    return EVENT_STATUS_META[status]?.label || EVENT_STATUS_META.requested.label;
}

// 'YYYY-MM-DD' is a calendar day, not an instant: build it in local time so it never slips a day.
export function parseEventDay(value) {
    if (!value) return null;
    const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

export function dayKey(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatEventDate(value, options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) {
    const day = parseEventDay(value);
    return day ? day.toLocaleDateString('en-US', options) : '';
}

export function formatEventTime(value) {
    if (!value) return '';
    const [hours, minutes] = String(value).split(':').map(Number);
    if (Number.isNaN(hours)) return '';
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const twelve = hours % 12 === 0 ? 12 : hours % 12;
    return `${twelve}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
}

export function eventTimeRange(event) {
    const start = formatEventTime(event.start_time);
    const end = formatEventTime(event.end_time);
    if (start && end) return `${start} – ${end}`;
    return start || end || 'Time to be set';
}

// `today` is a Date (the browser's day) or a 'YYYY-MM-DD' calendar day, e.g. today in the tenant's time zone (eventsBoard.todayKey).
export function isUpcomingEvent(event, today = new Date()) {
    if (typeof today === 'string') return Boolean(event.event_date) && String(event.event_date).slice(0, 10) >= today;
    const day = parseEventDay(event.event_date);
    if (!day) return false;
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return day.getTime() >= start.getTime();
}

// The weeks (Sunday first) that cover a month, as arrays of Date.
export function monthWeeks(year, month) {
    const first = new Date(year, month, 1);
    const cursor = new Date(year, month, 1 - first.getDay());
    const weeks = [];
    do {
        const week = [];
        for (let i = 0; i < 7; i += 1) {
            week.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        weeks.push(week);
    } while (cursor.getMonth() === month || weeks.length < 4);
    return weeks;
}

// Turn a starter kit's checklist into editable notes text.
export function checklistNotes(kit) {
    const items = Array.isArray(kit?.default_checklist) ? kit.default_checklist : [];
    return items.map((item) => `☐ ${item}`).join('\n');
}

export function formFromEvent(event) {
    return {
        event_name: event.event_name || '',
        event_date: event.event_date || '',
        end_date: event.end_date || '',
        event_type_other: event.event_type_other || '',
        start_time: (event.start_time || '').slice(0, 5),
        end_time: (event.end_time || '').slice(0, 5),
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        event_type: event.event_type || '',
        expected_guest_count: event.expected_guest_count ?? '',
        notes: event.notes || '',
        entity_id: event.engagement?.entity_id || '',
    };
}
